import { useEffect, type JSX } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Center, Loader, Text, Stack } from '@mantine/core';
import { getSupabase } from './supabase-client';

/**
 * Handles OAuth redirect and magic-link token exchange.
 * Supabase's detectSessionInUrl:true handles URL hash/query automatically.
 * We just wait for the session to be established, then redirect.
 */
export function CallbackPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const supabase = getSupabase();

    // Give Supabase time to process the URL tokens (detectSessionInUrl handles it)
    const checkSession = async (): Promise<void> => {
      // Wait for session to be available (Supabase processes URL hash automatically)
      const { data, error } = await supabase.auth.getSession();

      if (error) {
        console.error('[auth/callback] session error:', error.message);
        navigate('/login', { replace: true });
        return;
      }

      if (data.session) {
        const from = searchParams.get('from') ?? '/';
        navigate(from, { replace: true });
      } else {
        // Retry once — token exchange may still be in progress
        setTimeout(async () => {
          const { data: retryData } = await supabase.auth.getSession();
          const from = searchParams.get('from') ?? '/';
          navigate(retryData.session ? from : '/login', { replace: true });
        }, 1000);
      }
    };

    void checkSession();
  }, [navigate, searchParams]);

  return (
    <Center h="100vh">
      <Stack align="center">
        <Loader aria-label="Completing sign-in..." />
        <Text c="dimmed" size="sm">
          Completing sign-in…
        </Text>
      </Stack>
    </Center>
  );
}
